import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Download, FileText, Trash2 } from 'lucide-react';

import type { SolicitationEvidenceProps } from '@/domain/models/solicitation-evidence.js';
import { formatDate } from '@/shared/utils/format-date.js';

interface SolicitationEvidenceListProps {
    evidences: SolicitationEvidenceProps[];
    canRemove: boolean;
    removing?: boolean;
    onDownload: (docId: string) => void;
    onRemove: (docId: string) => void;
    labels: {
        attachments: string;
        downloadAttachment: string;
        removeAttachment: string;
        resolutionBadge: string;
    };
}

export function SolicitationEvidenceList({ evidences, canRemove, removing = false, onDownload, onRemove, labels }: SolicitationEvidenceListProps) {
    if (evidences.length === 0) {
        return null;
    }

    return (
        <Box data-testid="solicitation-evidence-list">
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {labels.attachments} ({evidences.length})
            </Typography>
            <Stack spacing={0.5}>
                {evidences.map((evidence) => (
                    <Stack key={evidence.id} direction="row" alignItems="center" spacing={1} data-testid={`solicitation-evidence-item-${evidence.id}`}>
                        <FileText size={16} style={{ flexShrink: 0, opacity: 0.6 }} />
                        <Typography variant="body2" noWrap title={evidence.fileName} sx={{ flexGrow: 1, minWidth: 0 }}>
                            {evidence.fileName}
                        </Typography>
                        {evidence.context === 'RESOLUCAO' ? (
                            <Chip size="small" variant="outlined" label={labels.resolutionBadge} data-testid={`solicitation-evidence-context-badge-${evidence.id}`} />
                        ) : null}
                        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                            {formatDate(evidence.uploadedAt)}
                        </Typography>
                        <Tooltip title={labels.downloadAttachment}>
                            <IconButton
                                size="small"
                                aria-label={labels.downloadAttachment}
                                data-testid={`solicitation-evidence-download-${evidence.id}`}
                                onClick={() => onDownload(evidence.id)}
                            >
                                <Download size={16} />
                            </IconButton>
                        </Tooltip>
                        {canRemove && evidence.context === 'ABERTURA' ? (
                            <Tooltip title={labels.removeAttachment}>
                                <span>
                                    <IconButton
                                        size="small"
                                        aria-label={labels.removeAttachment}
                                        disabled={removing}
                                        data-testid={`solicitation-evidence-remove-${evidence.id}`}
                                        onClick={() => onRemove(evidence.id)}
                                    >
                                        <Trash2 size={16} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        ) : null}
                    </Stack>
                ))}
            </Stack>
        </Box>
    );
}
